// src/Components/GoogleMap/PolylineDraw.js

import { useState, useRef, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_COLOR = '#00B0FF';
const DEFAULT_WEIGHT = 4;

export function usePolylineDraw(
  mapInstance,
  setPolylinesData,
  setSelectedMarkerData,
  onDebugUpdate,
  setActivePolylineId,
  enterLineEditMode,
  drawingStyles, // AMENDED: Receive drawingStyles to apply custom colors to the draft
) {
  const [isDrawingLine, setIsDrawingLine] = useState(false);
  const isDrawingLineRef = useRef(false);

  const linePoints = useRef([]);
  const drawingPolyline = useRef(null);
  const rubberBand = useRef(null);
  const tempVerticesRef = useRef([]);
  const drawingStartTimeRef = useRef(0);

  // We use this to store the raw DOM event handler so we can remove it later
  const domDblClickHandlerRef = useRef(null);

  const tempIdRef = useRef(null);
  const setSelectedMarkerDataRef = useRef(setSelectedMarkerData);

  // AMENDED: Keep a perfectly fresh ref of the styles to avoid stale closures during click events
  const drawingStylesRef = useRef(drawingStyles);

  useEffect(() => {
    drawingStylesRef.current = drawingStyles;
  }, [drawingStyles]);

  useEffect(() => {
    isDrawingLineRef.current = isDrawingLine;
  }, [isDrawingLine]);

  useEffect(() => {
    setSelectedMarkerDataRef.current = setSelectedMarkerData;
  }, [setSelectedMarkerData]);

  // --- HELPER: Create Advanced Marker Handle ---
  const createVertexHandle = useCallback((color) => {
    const div = document.createElement('div');
    // AMENDED: INCREASED SIZE 100% (4px -> 8px)
    div.style.width = '8px';
    div.style.height = '8px';
    div.style.backgroundColor = color;
    // THINNER BORDER: 1.5px -> 1px
    div.style.border = '1px solid white';
    div.style.borderRadius = '50%';
    div.style.boxShadow = '0 1px 2px rgba(0,0,0,0.3)';
    div.style.pointerEvents = 'none'; // mimic clickable: false
    return div;
  }, []);

  const getDashIcons = (isDashed, dashColor, dashLength, weight) => {
    if (!isDashed) return [];
    const length = dashLength || 10;
    return [
      {
        icon: {
          path: 'M 0,-1 0,1',
          strokeColor: dashColor || '#000000',
          strokeWeight: weight,
          strokeOpacity: 1,
          scale: length / 2,
        },
        offset: '0',
        repeat: `${length * 2}px`,
      },
    ];
  };

  // AMENDED: Live updating effect so the drawing line changes color and dash instantly mid-draw
  useEffect(() => {
    if (isDrawingLineRef.current) {
      const activeColor = drawingStyles?.lineColor || DEFAULT_COLOR;
      const activeWeight =
        drawingStyles?.lineWeight !== undefined
          ? drawingStyles.lineWeight
          : DEFAULT_WEIGHT;

      const icons = getDashIcons(
        drawingStyles?.isDashed,
        drawingStyles?.dashColor,
        drawingStyles?.dashLength,
        activeWeight,
      );

      if (drawingPolyline.current) {
        drawingPolyline.current.setOptions({
          strokeColor: activeColor,
          strokeWeight: activeWeight,
          icons: icons,
        });
      }
      if (rubberBand.current) {
        rubberBand.current.setOptions({
          strokeColor: activeColor,
        });
      }

      tempVerticesRef.current.forEach((v) => {
        if (v) v.content = createVertexHandle(activeColor);
      });
    }
  }, [
    drawingStyles?.lineColor,
    drawingStyles?.lineWeight,
    drawingStyles?.isDashed,
    drawingStyles?.dashColor,
    drawingStyles?.dashLength,
    createVertexHandle,
  ]);

  // --- STOP/CANCEL ---
  const stopDrawingLine = useCallback(
    () => {
      setIsDrawingLine(false);
      isDrawingLineRef.current = false;
      linePoints.current = [];
      tempIdRef.current = null;

      if (drawingPolyline.current) drawingPolyline.current.setMap(null);
      if (rubberBand.current) rubberBand.current.setMap(null);

      // --- NUCLEAR OPTION: Remove the DOM Listener ---
      if (mapInstance.current && domDblClickHandlerRef.current) {
        const mapDiv = mapInstance.current.getDiv();
        mapDiv.removeEventListener('dblclick', domDblClickHandlerRef.current, true);
        domDblClickHandlerRef.current = null;
      }

      // Cleanup Vertices (AdvancedMarkers)
      tempVerticesRef.current.forEach((v) => {
        if (v) v.map = null;
      });
      tempVerticesRef.current = [];

      if (mapInstance.current) {
        mapInstance.current.setOptions({ draggableCursor: 'grab' });

        // Re-enable API zoom just in case, but safe behind a delay
        setTimeout(() => {
          if (mapInstance.current) {
            mapInstance.current.setOptions({ disableDoubleClickZoom: false });
          }
        }, 800);
      }

      // if (onDebugUpdate) {
      //   onDebugUpdate({
      //     stage: 'STOP DRAWING',
      //     points: 0,
      //     cursor: 'Idle',
      //     lastPoint: '-',
      //     rubberBand: 'None',
      //   });
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapInstance, onDebugUpdate],
  );

  const cancelDrawingLine = useCallback(() => stopDrawingLine(), [stopDrawingLine]);

  // --- COMPLETE ---
  const completePolyline = useCallback(
    (google, skipStateUpdate = false) => {
      // AMENDED: Bypass the 500ms block if we are forcefully saving from the Drawer!
      if (!skipStateUpdate && Date.now() - drawingStartTimeRef.current < 500) return null;

      if (linePoints.current.length < 2) {
        cancelDrawingLine();
        return null;
      }

      const finalPath = [...linePoints.current];
      const newId = tempIdRef.current || uuidv4();

      // AMENDED: Ensure the completed polyline data matches the currently selected drawing styles from ref
      const finalColor = drawingStylesRef.current?.lineColor || DEFAULT_COLOR;
      const finalWeight =
        drawingStylesRef.current?.lineWeight !== undefined
          ? drawingStylesRef.current.lineWeight
          : DEFAULT_WEIGHT;

      const finalIsDashed = drawingStylesRef.current?.isDashed || false;
      const finalDashColor = drawingStylesRef.current?.dashColor || '#000000';
      const finalDashLength = drawingStylesRef.current?.dashLength || 10;

      const newLineData = {
        id: newId,
        path: finalPath,
        color: finalColor,
        weight: finalWeight,
        isDashed: finalIsDashed,
        dashColor: finalDashColor,
        dashLength: finalDashLength,
        type: 'line',
        isNew: true,
        lastModified: new Date().toISOString(),
      };

      if (!skipStateUpdate) {
        setPolylinesData((prev) => [...prev, newLineData]);
      }

      stopDrawingLine();

      if (!skipStateUpdate) {
        setActivePolylineId(newId);

        if (setSelectedMarkerDataRef.current)
          setSelectedMarkerDataRef.current(newLineData);

        setTimeout(() => {
          if (enterLineEditMode) enterLineEditMode(newId);
        }, 100);
      }

      return newLineData;
    },
    [
      stopDrawingLine,
      cancelDrawingLine,
      setPolylinesData,
      setActivePolylineId,
      enterLineEditMode,
    ],
  );

  // --- START ---
  const startDrawingLine = useCallback(
    (google) => {
      const g = google || window.google;
      if (!mapInstance.current || !g) return;

      setIsDrawingLine(true);
      isDrawingLineRef.current = true;
      linePoints.current = [];

      tempVerticesRef.current.forEach((v) => {
        if (v) v.map = null;
      });
      tempVerticesRef.current = [];

      drawingStartTimeRef.current = Date.now();
      tempIdRef.current = uuidv4();

      if (drawingPolyline.current) drawingPolyline.current.setMap(null);
      if (rubberBand.current) rubberBand.current.setMap(null);

      // AMENDED: Use actual styles for the draft lines directly from ref
      const activeColor = drawingStylesRef.current?.lineColor || DEFAULT_COLOR;
      const activeWeight =
        drawingStylesRef.current?.lineWeight !== undefined
          ? drawingStylesRef.current.lineWeight
          : DEFAULT_WEIGHT;

      const activeIsDashed = drawingStylesRef.current?.isDashed || false;
      const activeDashColor = drawingStylesRef.current?.dashColor || '#000000';
      const activeDashLength = drawingStylesRef.current?.dashLength || 10;

      const icons = getDashIcons(
        activeIsDashed,
        activeDashColor,
        activeDashLength,
        activeWeight,
      );

      // 1. Main Line
      drawingPolyline.current = new g.maps.Polyline({
        map: mapInstance.current,
        strokeColor: activeColor,
        strokeWeight: activeWeight,
        icons: icons,
        zIndex: 30,
        clickable: false,
      });

      // 2. Rubberband (Disabled/Hidden)
      rubberBand.current = new g.maps.Polyline({
        map: mapInstance.current,
        strokeColor: activeColor,
        strokeOpacity: 0.5,
        strokeWeight: 2,
        zIndex: 30,
        clickable: false,
      });

      if (mapInstance.current) {
        mapInstance.current.setOptions({ draggableCursor: 'crosshair' });

        // Disable API Zoom as a first layer of defense
        mapInstance.current.setOptions({ disableDoubleClickZoom: true });

        // --- NUCLEAR OPTION: Add DOM Capture Listener ---
        const mapDiv = mapInstance.current.getDiv();

        const handleDomDblClick = (e) => {
          // Kill the event completely
          e.stopPropagation();
          e.preventDefault();

          // Trigger completion
          completePolyline();
        };

        // 'true' enables Capture Phase (happens before bubbling)
        mapDiv.addEventListener('dblclick', handleDomDblClick, true);
        domDblClickHandlerRef.current = handleDomDblClick;
      }

      if (setSelectedMarkerDataRef.current) {
        setSelectedMarkerDataRef.current({
          id: tempIdRef.current,
          type: 'line',
          path: [],
          color: activeColor,
          weight: activeWeight,
          isDashed: activeIsDashed,
          dashColor: activeDashColor,
          dashLength: activeDashLength,
          isDrawing: true,
          isNew: true,
        });
      }

      // if (onDebugUpdate) {
      //   onDebugUpdate({
      //     stage: 'START LINE',
      //     points: 0,
      //     cursor: 'Waiting',
      //     lastPoint: 'None',
      //     rubberBand: 'Active',
      //   });
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapInstance, onDebugUpdate, completePolyline],
  );

  // --- CLICK ---
  const handleLineDrawClick = useCallback(
    (latLng) => {
      if (!isDrawingLineRef.current || !latLng) return;

      const pos = latLng.toJSON();
      const google = window.google;

      if (linePoints.current.length > 0) {
        const last = linePoints.current[linePoints.current.length - 1];
        if (last.lat === pos.lat && last.lng === pos.lng) return;
      }

      linePoints.current.push(pos);

      if (drawingPolyline.current) {
        const path = drawingPolyline.current.getPath();
        path.push(latLng);
      }

      // AMENDED: Ensure the point handles match the active color dynamically from ref
      const activeColor = drawingStylesRef.current?.lineColor || DEFAULT_COLOR;

      // Drop Vertex Marker (UPDATED to AdvancedMarkerElement with smaller handle)
      if (google && google.maps && google.maps.marker) {
        const vertex = new google.maps.marker.AdvancedMarkerElement({
          position: latLng,
          map: mapInstance.current,
          content: createVertexHandle(activeColor),
          zIndex: 40,
        });
        tempVerticesRef.current.push(vertex);
      }

      // if (onDebugUpdate) {
      //   onDebugUpdate({
      //     stage: 'POINT ADDED',
      //     points: linePoints.current.length,
      //     cursor: `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`,
      //     lastPoint: 'Fixed',
      //     rubberBand: 'Active',
      //   });
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapInstance, onDebugUpdate, createVertexHandle],
  );

  const handleLineMouseMove = useCallback((latLng) => {
    return;
  }, []);

  return {
    isDrawingLine,
    isDrawingLineRef,
    startDrawingLine,
    cancelDrawingLine,
    handleLineDrawClick,
    handleLineMouseMove,
    completePolyline,
  };
}

/*
// ==============================================================================
// AMENDED: DEBUG HUD COMPONENT
// Moved from MapOverlays.js. Uncomment to restore heads-up-display during drawing
// ==============================================================================
// import Paper from '@mui/material/Paper';
//
// export const PolylineDebugHUD = ({ showDebug }) => {
//   if (!showDebug) return null;
//   return (
//     <Paper
//       id='debug-hud'
//       sx={{
//         position: 'absolute',
//         top: 10,
//         left: 10,
//         padding: 1.5,
//         bgcolor: 'rgba(0, 0, 0, 0.8)',
//         color: '#81c784',
//         fontFamily: 'monospace',
//         fontSize: 12,
//         zIndex: 10000,
//         pointerEvents: 'none',
//         minWidth: '200px',
//       }}>
//       Loading Debug Info...
//     </Paper>
//   );
// };
*/
